import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import App from './App';
import Spinner from "react-bootstrap/Spinner";
import MetaHelmet from './components/MetaHelmet';
import { getMetaTags } from './utils/metaConfig';
import { useAiBrandingData } from './hooks/useEditionData';
import { useMLO } from './MLOContext';

const LoanOfficerPage = () => {
  const { loanOfficerName, editionName: paramEditionName } = useParams();
  const { setMloData, setIsInfluencer: setContextIsInfluencer } = useMLO();
  const editionName = paramEditionName ? paramEditionName.toLowerCase() : null;
  const aiBrandingData = useAiBrandingData();

  const [pageData, setPageData] = useState(null);
  const [loanOfficerData, setLoanOfficerData] = useState(null);
  const [isInfluencer, setIsInfluencer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetchRef = useRef(false);

  useEffect(() => {
    if (!editionName || !loanOfficerName || fetchRef.current) return;
    fetchRef.current = true;

    const fetchAll = async () => {
      try {
        const basePath = process.env.REACT_APP_MASTERMIND_EDITION_JSON_PATH
          || `${process.env.REACT_APP_CDN_LINK}mastermind/homepage/jsons/`;
        const editionRes = await axios.get(`${basePath}${editionName}.json?t=${Date.now()}`);
        const editionData = editionRes.data;
        const editionConfig = editionData.config || {};

        let influencersList = [];
        try {
          const res = await axios.get(`${process.env.REACT_APP_CDN_LINK}mastermind/homepage/jsons/influencer/influencers-list.json?t=${Date.now()}`);
          influencersList = (res.data || []).filter(Boolean);
        } catch { /* fallback empty */ }

        const isInfluencerPerson = influencersList.includes(loanOfficerName.toLowerCase());

        if (isInfluencerPerson) {
          if (editionConfig.enableInfluencerPages === false) throw new Error('Influencer pages disabled');

          const res = await axios.get(`${process.env.REACT_APP_CDN_LINK}mastermind/homepage/jsons/influencer/${loanOfficerName}.json?t=${Date.now()}`);
          const personData = Array.isArray(res.data) ? res.data[0] : res.data;
          if (!personData?.name) throw new Error('Invalid influencer data');

          setPageData({
            ...editionData,
            banner: { ...editionData.banner, influencer: personData },
            morganscan: { ...editionData.morganscan, ...personData.whysunwest },
          });
          setLoanOfficerData(personData);
          setIsInfluencer(true);
          setMloData(personData);
          setContextIsInfluencer(true);
        } else {
          if (editionConfig.enableMLOPages === false) throw new Error('MLO pages disabled');

          const res = await axios.get(`${process.env.REACT_APP_MLO_JSONS}${loanOfficerName}.json`);
          const personData = Array.isArray(res.data) ? res.data[0] : res.data;
          if (!personData?.name) throw new Error('Invalid MLO data');

          const formattedName = loanOfficerName.toLowerCase().replace(/\s+/g, '');
          const loData = {
            ...personData,
            angelaiUrl: personData.angelaiUrl,
            loanOfficer: { ...personData, formattedName },
          };

          setPageData({
            ...editionData,
            banner: { ...editionData.banner, loanOfficer: loData.loanOfficer },
            morganscan: { ...editionData.morganscan, ...personData.whysunwest },
          });
          setLoanOfficerData(loData);
          setIsInfluencer(false);
          setMloData(loData);
          setContextIsInfluencer(false);
        }

        setLoading(false);
      } catch {
        setError(true);
        setLoading(false);
      }
    };

    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editionName, loanOfficerName]);

  useEffect(() => {
    if (!error || loading) return;
    if (editionName) {
      window.location.replace(`/masterminds/${editionName}`);
    } else {
      const now = new Date();
      const months = ["january","february","march","april","may","june","july","august","september","october","november","december"];
      window.location.replace(`/masterminds/${months[now.getMonth()]}${now.getFullYear()}`);
    }
  }, [error, loading, editionName]);

  if (loading || !pageData || !aiBrandingData || !loanOfficerData) {
    return <Spinner animation="border" role="status" />;
  }

  const meta = getMetaTags({
    pageType: isInfluencer ? 'influencer' : 'mlo',
    editionName,
    personName: loanOfficerName,
    personData: loanOfficerData,
    metaData: pageData.metaData,
    domain: process.env.REACT_APP_DOMAIN,
    cdnLink: process.env.REACT_APP_CDN_LINK,
    publicUrl: process.env.PUBLIC_URL,
  });

  return (
    <>
      <MetaHelmet {...meta} />
      <App
        pageData={pageData}
        aiBrandingData={aiBrandingData}
        isLoanOfficerPage={!isInfluencer}
        loanOfficerData={!isInfluencer ? loanOfficerData : null}
        isInfluencerPage={isInfluencer}
        influencerData={isInfluencer ? loanOfficerData : null}
      />
    </>
  );
};

export default LoanOfficerPage;
